










































































































/**
 * LabelCanvas
 * Interactive SVG surface: renders labels and staves + captures/enriches/re-emits events.
 * Behaviors are dynamically injected through slots + listeners (canvas.on / canvas.emit).
 */
import type { PropType } from 'vue';
import {
  defineComponent, ref, computed, onMounted, onUnmounted,
} from 'vue';
import LabelGroupSvg from '@/vuejs/components/canvas/LabelGroupSvg.vue';
import StavesZones from '@/vuejs/components/canvas/StavesZones.vue';
import PixelUtils from '@/data/PixelUtils';
import type {
  Label, LabelId, StaffId, Pixel, Source,
} from '@/data';
import type {
  BaseEventName,
  CanvasMouseEvent,
  EventName,
  SourceTag,
  CanvasEventSource,
} from '@/components/canvas/canvas-events';
import {
  canvasEventIsModifiable,
  canvasSourceIsLabel,
} from '@/components/canvas/canvas-events';

const LINES_HEIGHT = 102;

export default defineComponent({
  name: 'LabelCanvas',
  components: { LabelGroupSvg, StavesZones },
  props: {
    selectedId: { type: Number as PropType<LabelId | null>, default: null },
    // eslint-disable-next-line vue/no-boolean-default
    renderStaves: { type: Boolean, default: true },
    isFocused: { type: Boolean, required: true },
    // eslint-disable-next-line vue/require-default-prop
    getLabel: {
      required: false,
      type: Function as PropType<(labelId: LabelId) => Readonly<Label> | null>,
    },
    sortedLabels: { type: Array as () => Label[], required: true },
    source: { type: Object as () => Source, required: true },
    zoom: { type: Number, default: 1 },
    currentViewX: { type: Number, default: 0 },
    showLines: { type: Boolean, required: false },
    // eslint-disable-next-line vue/no-boolean-default
    showImage: { type: Boolean, default: true },
  },
  setup(props, { emit }) {
    const svg = ref<SVGSVGElement | null>(null);
    const currentPage = ref(0);

    /**
     * Public API: The canvas event system
     * Allows behaviors (slots) to subscribe to canvas events dynamically
     */
    const canvasListeners = new Map<string, Set<(...args: any[]) => void>>();
    const canvas: CanvasEventSource = {
      on: (event, callback) => {
        if (!canvasListeners.has(event)) {
          canvasListeners.set(event, new Set());
        }
        canvasListeners.get(event)!.add(callback);
      },
      off: (event, callback) => {
        const cbs = canvasListeners.get(event);
        if (cbs) {
          cbs.delete(callback);
          if (cbs.size === 0) canvasListeners.delete(event);
        }
      },
      emit: (event, ...args) => {
        emit(event, ...args); // also trigger Vue $emit
        const cbs = canvasListeners.get(event);
        if (cbs) {
          // eslint-disable-next-line no-restricted-syntax
          for (const cb of cbs) cb(...args);
        }
      },
    };

    const canvasHeight = computed(() => {
      if (!props.renderStaves) return props.source.height;
      return props.source.pages
        ? props.source.pages.length * props.source.height + LINES_HEIGHT * 2
        : props.source.height + LINES_HEIGHT * 2;
    });

    const canvasWidth = computed(() => props.source.width);

    /**
     * Check if the SVG is fully rendered and visible
     * (avoids emitting events while loading)
     */
    const canEmit = () => !!svg.value?.clientHeight;

    /**
     * Transform native MouseEvent into a CanvasMouseEvent (SVG coordinates)
     * Convert screen coordinates to SVG coordinates
     */
    const toCanvasEvent = (event: MouseEvent): CanvasMouseEvent => {
      if (!svg.value) throw new Error('Canvas not available');
      // https://stackoverflow.com/questions/10298658/mouse-position-inside-autoscaled-svg

      // Create an SVGPoint at mouse position
      const pt = svg.value.createSVGPoint();
      pt.x = event.clientX;
      pt.y = event.clientY;

      const screenMatrix = svg.value.getScreenCTM();
      if (!screenMatrix) throw new Error('Could not convert mouse position');

      const { x, y } = pt.matrixTransform(screenMatrix.inverse());
      return {
        inner: event,
        x: x / props.zoom as Pixel,
        y: y / props.zoom as Pixel,
      };
    };

    /**
     * Get the event name with modifiers (ctrl, shift, alt)
     */
    const getModifiers = (e: MouseEvent | KeyboardEvent | FocusEvent): EventName => {
      const name = e.type as BaseEventName;
      if (!('ctrlKey' in e) || !canvasEventIsModifiable(name)) return name;
      const mod = [
        e.ctrlKey ? 'ctrl' : '',
        e.shiftKey ? 'shift' : '',
        e.altKey ? 'alt' : '',
      ].filter(Boolean).join('-');
      return (mod ? `${mod}-` : '') + name as EventName;
    };

    /**
     * Handle an event coming from the SVG itself
     * Enrich it (coords, label, staff) and re-emit it using the canvas event system
     */
    const onCanvasEvent = (e: MouseEvent | KeyboardEvent | FocusEvent) => {
      if (!canEmit()) return;

      const target = e.target as SVGElement;
      const tag = target.dataset.canvasTag as SourceTag | undefined;
      const id = target.dataset.canvasId;

      // If the event is not from the correct elements, return
      // If the canvas is loading, return
      // Only react to canvas-tagged elements
      if (!tag || !svg.value) return;

      // The payload to send to the behaviors
      const payload: unknown[] = [];

      // Add the event to the payload
      // If it's a mouse event, wrap it with more info
      if (e instanceof MouseEvent) payload.push(toCanvasEvent(e));
      else payload.push(e);

      // The staffId if it is recovered later
      let staffId: StaffId | null = null;

      // If the event comes from a label element, get and push the label in the payload
      if (canvasSourceIsLabel(tag)) {
        // Ignore label events if getLabel is not passed
        if (!props.getLabel) return;
        const labelId: LabelId = tag === 'label'
          ? parseInt(id as string, 10)
          : props.selectedId! as LabelId;
        const label = props.getLabel(labelId);
        if (!label) {
          // eslint-disable-next-line no-console
          console.log(`Label ${labelId} is gone, ignoring event`);
          return;
        }

        payload.push(label);
        // Add the staff id so the staff can be recovered
        staffId = label.staffId;
      } else if (tag === 'staff') {
        // Parse the staff id directly
        staffId = parseInt(id as string, 10);
      }

      // Add the corresponding staff if found
      if (staffId !== null) {
        const staff = props.source.staves[staffId]
          || PixelUtils.getStaffByPosition(props.source.pages || [], staffId)
          || props.source.staves[0];
        payload.push(staff);
      }

      // Transform events from things like 'mousedown' to 'shift-mousedown'
      const fullName = getModifiers(e);

      // Emit two events:
      // - Specific (example: label-click)
      // - Global (example: all-click)
      (canvas.emit as any)(`${tag}-${fullName}`, ...payload);
      (canvas.emit as any)(`all-${fullName}`, ...payload);
    };

    /**
     * Static list of SVG events we listen to.
     * Bound directly via v-on="listeners" in the template
     * This object is a list of events to listen to, and their listener
     * The listener re-emits the event from the canvas with a new name
     * The listener also adds a relevant payload to each event
     */
    const listeners: Record<`!${BaseEventName}`, unknown> = {
      '!click': onCanvasEvent,
      '!dblclick': onCanvasEvent,
      '!mouseup': onCanvasEvent,
      '!mousedown': onCanvasEvent,
      '!mousemove': onCanvasEvent,
      '!mouseover': onCanvasEvent,
      '!wheel': onCanvasEvent,
      '!keydown': onCanvasEvent,
      '!keyup': onCanvasEvent,
      '!focusin': onCanvasEvent,
    };

    /**
     * Global document-level listener
     * Useful to handle keyboard shortcuts even if outside the SVG
     */
    const reemit = (e: MouseEvent | KeyboardEvent | FocusEvent) => {
      // Skipping events if canvas is not loaded
      if (!canEmit()) return;

      // Ignore keyboard events on input elements
      if ((e.type === 'keydown' || e.type === 'keyup') && ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'].includes((e.target as HTMLElement).tagName)) return;
      const packet = e instanceof MouseEvent ? toCanvasEvent(e) : e;
      (canvas.emit as any)(`document-${getModifiers(e)}`, packet);
    };

    onMounted(() => {
      // Attach global listeners
      const opts = { capture: true, passive: false };
      document.addEventListener('click', reemit, opts);
      document.addEventListener('wheel', reemit, opts);
      document.addEventListener('dblclick', reemit, opts);
      document.addEventListener('mouseup', reemit, opts);
      document.addEventListener('mousedown', reemit, opts);
      document.addEventListener('mousemove', reemit, opts);
      document.addEventListener('keydown', reemit, opts);
      document.addEventListener('keyup', reemit, opts);
      document.addEventListener('focusin', reemit, opts);
    });

    onUnmounted(() => {
      // Detach global listeners
      const opts = { capture: true };
      document.removeEventListener('click', reemit, opts);
      document.removeEventListener('wheel', reemit, opts);
      document.removeEventListener('dblclick', reemit, opts);
      document.removeEventListener('mouseup', reemit, opts);
      document.removeEventListener('mousedown', reemit, opts);
      document.removeEventListener('mousemove', reemit, opts);
      document.removeEventListener('keydown', reemit, opts);
      document.removeEventListener('keyup', reemit, opts);
      document.removeEventListener('focusin', reemit, opts);
    });

    return {
      svg,
      canvas,
      canvasHeight,
      canvasWidth,
      currentPage,
      listeners,
      LINES_HEIGHT,
    };
  },
});
